import { isReviewItemResolved } from "@utils/reportVisibility";
import { useState } from "react";

import { CardContent } from "@fronterahealth/frontera-ui-components/ui/card";
import { Separator } from "@fronterahealth/frontera-ui-components/ui/separator";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { CommentActionResolve } from "@components/QaReview/components/CommentActionResolve";
import { CommentActions } from "@components/QaReview/components/CommentActions";
import { CommentComposer } from "@components/QaReview/components/CommentComposer";
import { CommentMessage } from "@components/QaReview/components/CommentMessage";
import { CommentReply } from "@components/QaReview/components/CommentReply";
import { ResolvedCommentSummary } from "@components/QaReview/components/ResolvedCommentSummary";
import { useCloseThreadDrafts } from "@components/QaReview/hooks/useCloseThreadDrafts";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";

interface CommentThreadProps {
  root: ReviewItemFieldsFragment;
  replies: ReviewItemFieldsFragment[];
  isActive?: boolean;
  onActivate?: () => void;
}

export const CommentThread: React.FC<CommentThreadProps> = ({ root, replies, isActive, onActivate }) => {
  const { state, dispatch } = useReviewSession();
  const closeThreadDrafts = useCloseThreadDrafts();
  const [expandedAt, setExpandedAt] = useState<ReviewItemFieldsFragment["resolvedAt"]>();
  const replyComposer = state.composer?.kind === "reply" && state.composer.parentId === root.id ? state.composer : null;

  const rootResolved = isReviewItemResolved(root);
  const resolvedExpanded = rootResolved && expandedAt !== undefined && expandedAt === root.resolvedAt;
  const showThread = !rootResolved || resolvedExpanded;
  const showReplies = showThread && state.expandedReplies.includes(root.id);

  const toggleResolvedExpanded = () => {
    if (resolvedExpanded) {
      closeThreadDrafts(root.id);
      setExpandedAt(undefined);
    } else {
      dispatch({ type: "expandReplies", rootId: root.id });
      setExpandedAt(root.resolvedAt);
    }
  };

  const repliesShown = showReplies && replies.length > 0;
  const shownComposer = showThread ? replyComposer : null;
  // The separator and the rows under it come together: replies, the reply box, or both.
  const showThreadRows = repliesShown || !!shownComposer;

  // One card per thread: the root, then (when expanded) the replies and the reply box, so the active
  // state and the card edge cover the whole conversation.
  const threadCard = (
    <CommentMessage
      comment={root}
      depth={0}
      isActive={isActive}
      onActivate={onActivate}
      // Only the root gets the checkmark; resolving it closes the whole thread.
      headerAction={<CommentActionResolve comment={root} />}
    >
      {/* QA flags have no Edit, Reopen, Reply or replies, so they get no action row. */}
      {root.__typename === "ReportCommentType" && (
        <div className="px-1">
          <CommentActions comment={root} onActivate={onActivate} />
        </div>
      )}

      {showThreadRows && <Separator />}

      {showThreadRows && (
        <CardContent className="space-y-3">
          {repliesShown && replies.map((reply) => <CommentReply key={reply.id} reply={reply} />)}
          {shownComposer && <CommentComposer key={shownComposer.replyToId} target={shownComposer} embedded />}
        </CardContent>
      )}
    </CommentMessage>
  );

  return rootResolved ? (
    <ResolvedCommentSummary
      comment={root}
      isActive={isActive}
      onActivate={onActivate}
      expanded={resolvedExpanded}
      onToggleExpanded={toggleResolvedExpanded}
    >
      {threadCard}
    </ResolvedCommentSummary>
  ) : (
    threadCard
  );
};
